import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, thumbUrl } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { FileText, Search, ShieldCheck, BookmarkPlus, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { KindBadge, KIND_COLOR } from "../components/KindBadge";

function CaseRow({ c, kind }) {
  const { t } = useI18n();
  const title = kind === "ante" ? (c.full_name || "Missing person") : (c.reference_code || "Unidentified case");
  const path = `/${kind === "ante" ? "ante-mortem" : "post-mortem"}/${c.id}`;
  return (
    <Link to={path} className="block" data-testid={`browse-row-${kind}-${c.id}`}>
      <div className="flex gap-4 rounded-lg border border-[#E2E8F0] bg-white p-4 shadow-sm transition-base hover:border-[#2E3A46]/40" style={{ borderLeft: `3px solid ${KIND_COLOR[kind]}` }}>
        <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-md border border-[#E2E8F0] bg-[#F8F9FA]">
          {(c.photo_path || c.photo_paths?.[0]) ? (
            <img src={thumbUrl(c.photo_path || c.photo_paths[0])} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="grid h-full w-full place-items-center text-[#6B7F82]"><FileText size={18} /></div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-heading text-sm font-semibold text-[#1A2024]">{title}</span>
            <KindBadge kind={kind} />
          </div>
          <div className="font-body text-xs text-[#5C6873]">{c.country}{c.location ? `, ${c.location}` : ""} · {c.event_date || "date unknown"}</div>
          <div className="font-body text-xs text-[#5C6873]">{c.gender !== "unknown" ? c.gender : "gender unknown"} · age {c.age_min ?? "?"}-{c.age_max ?? "?"}</div>
          {kind === "post" && c.contributor_org && <div className="font-body text-xs font-medium text-[#4A7C59]" data-testid={`browse-org-${c.id}`}>{t("case.submitted_by")} {c.contributor_org}</div>}
          {c.description && <p className="mt-1 line-clamp-1 font-body text-xs text-[#5C6873]">{c.description}</p>}
        </div>
      </div>
    </Link>
  );
}

const PAGE_SIZE = 30;

export default function BrowsePage() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [ante, setAnte] = useState([]);
  const [post, setPost] = useState([]);
  const [totals, setTotals] = useState({ ante: 0, post: 0 });
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") || "";
  const tab = searchParams.get("tab") || "post";
  const [saved, setSaved] = useState([]);

  const setParam = (key, value) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (value) p.set(key, value); else p.delete(key);
      return p;
    }, { replace: true });
  };

  const fetchList = async (kind, offset, query) => {
    const ep = kind === "ante" ? "/ante-mortem" : "/post-mortem";
    const r = await api.get(ep, { params: { q: query || undefined, limit: PAGE_SIZE, offset } });
    return { items: r.data, total: parseInt(r.headers["x-total-count"] || `${r.data.length}`, 10) };
  };

  useEffect(() => {
    if (!user || user.role === "family") return;
    api.get("/saved-searches").then((r) => setSaved(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user || user.role === "family") return;
    const h = setTimeout(async () => {
      try {
        const [p, a] = await Promise.all([fetchList("post", 0, q), fetchList("ante", 0, q)]);
        setPost(p.items);
        setAnte(a.items);
        setTotals({ post: p.total, ante: a.total });
      } catch {
        // noop
      }
    }, q ? 300 : 0);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, q]);

  const loadMore = async (kind) => {
    setLoadingMore(true);
    try {
      const offset = kind === "ante" ? ante.length : post.length;
      const r = await fetchList(kind, offset, q);
      if (kind === "ante") setAnte((prev) => [...prev, ...r.items]);
      else setPost((prev) => [...prev, ...r.items]);
    } catch {
      // noop
    }
    setLoadingMore(false);
  };

  const saveSearch = async () => {
    const name = window.prompt(t("browse.name_prompt"), q || t("browse.save_search"));
    if (!name) return;
    try {
      const r = await api.post("/saved-searches", { name, query: q, tab });
      setSaved((prev) => [r.data, ...prev]);
      toast.success(t("browse.saved_ok"));
    } catch {
      toast.error("Error");
    }
  };

  const applySaved = (s) => {
    setSearchParams({ ...(s.query ? { q: s.query } : {}), tab: s.tab || "post" }, { replace: true });
  };

  const removeSaved = async (sid) => {
    try {
      await api.delete(`/saved-searches/${sid}`);
      setSaved((prev) => prev.filter((s) => s.id !== sid));
    } catch {
      toast.error("Error");
    }
  };

  if (!user) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-16 text-center" data-testid="browse-locked">
        <Search className="mx-auto text-[#6B7F82]" />
        <h2 className="mt-3 font-heading text-2xl font-semibold text-[#1A2024]">Sign in to browse cases</h2>
        <p className="mt-2 font-body text-sm text-[#5C6873]">For respect and privacy, browsing is reserved to verified accounts.</p>
      </div>
    );
  }

  if (user.role === "family") {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16 text-center" data-testid="browse-family-blocked">
        <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[#4A7C59]/12 text-[#4A7C59]">
          <ShieldCheck size={26} />
        </span>
        <h2 className="mt-5 font-heading text-2xl font-semibold text-[#1A2024]">We protect you from distressing content</h2>
        <p className="mx-auto mt-3 max-w-lg font-body text-sm text-[#5C6873]">
          To shield families from potentially painful images and false hope, EuroFind does not let you browse
          records of unidentified persons. Your report is compared confidentially on your behalf — if there is a
          relevant development, your dedicated liaison will contact you directly.
        </p>
        <Link to="/matches" className="mt-6 inline-block" data-testid="browse-family-cta">
          <button className="rounded-md bg-[#2E3A46] px-5 py-2.5 font-body text-sm text-white transition-base hover:bg-[#1A2024]">
            View your case status
          </button>
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-6 py-10" data-testid="browse-page">
      <p className="font-body text-xs uppercase tracking-[0.18em] text-[#6B7F82]">Database</p>
      <h1 className="mt-1 font-heading text-3xl font-semibold text-[#1A2024]">Browse cases</h1>
      <p className="mt-2 max-w-2xl font-body text-sm text-[#5C6873]">Search by name, country, city, physical traits or distinguishing marks.</p>

      <div className="mt-6 flex max-w-2xl flex-wrap items-center gap-2">
        <Input
          placeholder="Search…"
          value={q}
          onChange={(e) => setParam("q", e.target.value)}
          className="h-11 max-w-md border-[#E2E8F0] bg-white"
          data-testid="browse-search-input"
        />
        <Button variant="outline" className="h-11 border-[#E2E8F0] text-[#2E3A46]" onClick={saveSearch} data-testid="browse-save-search-btn">
          <BookmarkPlus size={16} className="mr-1" /> {t("browse.save_search")}
        </Button>
      </div>

      {saved.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="browse-saved-searches">
          <span className="font-body text-xs uppercase tracking-[0.14em] text-[#6B7F82]">{t("browse.saved")}</span>
          {saved.map((s) => (
            <span key={s.id} className="inline-flex items-center gap-1 rounded-full border border-[#E2E8F0] bg-white py-1 pl-3 pr-1.5 font-body text-xs text-[#2E3A46]" data-testid={`saved-search-${s.id}`}>
              <button onClick={() => applySaved(s)} className="font-medium hover:underline" data-testid={`saved-search-apply-${s.id}`}>{s.name}</button>
              <button onClick={() => removeSaved(s.id)} className="rounded-full p-0.5 text-[#8A96A0] hover:bg-[#F8F9FA] hover:text-[#9E4747]" data-testid={`saved-search-delete-${s.id}`}>
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      <Tabs value={tab} onValueChange={(v) => setParam("tab", v)} className="mt-6">
        <TabsList data-testid="browse-tabs">
          <TabsTrigger value="post" data-testid="tab-post" className="data-[state=active]:text-[#1F3A5F]">Unidentified bodies ({totals.post})</TabsTrigger>
          {user.role !== "family" && (
            <TabsTrigger value="ante" data-testid="tab-ante" className="data-[state=active]:text-[#4A7C59]">Missing persons ({totals.ante})</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="post" className="mt-4 space-y-3">
          {post.length === 0 && <p className="font-body text-sm text-[#5C6873]" data-testid="browse-post-empty">No records yet.</p>}
          {post.map((c) => <CaseRow key={c.id} c={c} kind="post" />)}
          {post.length < totals.post && (
            <Button variant="outline" className="border-[#E2E8F0] text-[#2E3A46]" disabled={loadingMore} onClick={() => loadMore("post")} data-testid="browse-post-load-more">
              {loadingMore ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
              {t("browse.load_more")} ({totals.post - post.length})
            </Button>
          )}
        </TabsContent>
        <TabsContent value="ante" className="mt-4 space-y-3">
          {ante.length === 0 && <p className="font-body text-sm text-[#5C6873]" data-testid="browse-ante-empty">No records yet.</p>}
          {ante.map((c) => <CaseRow key={c.id} c={c} kind="ante" />)}
          {ante.length < totals.ante && (
            <Button variant="outline" className="border-[#E2E8F0] text-[#2E3A46]" disabled={loadingMore} onClick={() => loadMore("ante")} data-testid="browse-ante-load-more">
              {loadingMore ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
              {t("browse.load_more")} ({totals.ante - ante.length})
            </Button>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
